import React, { useEffect, useState, useRef } from "react";

import { frameProcs, animations, previousTimeStamp } from "./animation";
import { createRenderer } from "./render";

import * as THREE from "three";
import * as topojson from "topojson-client";
//import Globe from "react-globe.gl";

import schema from "./schema/index";
import { borg } from "./map";
import { startWebsocket } from "./protocol";

import layout, {
  cycleLayoutMode,
  setLayoutRoot,
  setLineContainer,
  mainBounds,
} from "./layout/index";

import { setTargetHandlers, browseTarget, lockTarget } from "./crosshair";
import { setViewport, setTransform } from "./camera";

import { initialise, openItemMenu } from "./itemMenuOpener";
import InfoBox from "components/InfoBox";
import ItemMenu from "./ItemMenu";
import PerfHud from "./PerfHud";
import RendererToggle from "./RendererToggle";
import FilterPanel from "./FilterPanel";
import EffectsMenu from "./EffectsMenu";
import FpsCounter from "./FpsCounter";
import ThreadModal from "./ThreadModal";
import MemoryModal from "./MemoryModal";
import TunnelModal from "./TunnelModal";

const polygonsMaterial = new THREE.MeshLambertMaterial({
  color: "#e1a67a",
  side: THREE.DoubleSide,
});

let curLat = 0;
let curLng = 0;
let curAlt = 20;

let dstLat = 0;
let dstLng = 0;
let dstAlt = 1.5;

let resetTimeout = null;
let rot = true;
let previousTimestamp = null;

const BorgMap = () => {
  /*const globeEl = useRef();
  const globe = globeEl.current;
  const [points, setPoints] = useState([]);

  useEffect(() => {
    function globeAnimation(delta) {
      const globe = globeEl.current;

      if (globe && globe.pointOfView) {
        if (rot) dstLng += delta / 100;

        let ease = 0.1 * (delta / 50);

        if (ease < 0.0001) ease = 0.0001;
        if (ease > 0.5) ease = 0.5;

        const invEase = 1 - ease;

        curLat = curLat * invEase + dstLat * ease;
        curLng = curLng * invEase + dstLng * ease;
        curAlt = curAlt * invEase + dstAlt * ease;

        const trg = { lat: curLat, lng: curLng, altitude: curAlt };
        globe.pointOfView(trg, 0);
      }
    }

    frameProcs.globe = globeAnimation;
  }, []);

  const ipCache = {};

  function handleIpGeoResponse(ip, data) {
    ipCache[ip] = data;
    if (data.latitude && data.longitude) {
      const lat = data.latitude;
      const lng = data.longitude;

      dstLat = lat;
      dstLng = lng;
      dstAlt = 0.5;
      rot = false;

      if (resetTimeout) {
        clearTimeout(resetTimeout);
      }
      resetTimeout = setTimeout(() => {
        dstAlt = 1.5;
        dstLat = 0;
        rot = true;
        clearTimeout(resetTimeout);
        resetTimeout = null;
      }, 10000);

      const pt = {
        lat: lat - 0.1 + Math.random() * 0.2,
        lng: lng - 0.1 + Math.random() * 0.2,
        size: 0.1 + Math.random() * 0.01,
      };

      if (points.length > 5) {
        setPoints((points) => [pt, ...points.slice(0, 5)]);
      } else {
        setPoints((points) => [pt, ...points]);
      }

      setTimeout(() => {
        setPoints((points) => [...points.filter((p) => p != pt)]);
      }, 200);
    }
  }

  function pingIp(ip, type) {
    if (
      ip.startsWith("10.") ||
      ip.startsWith("127.0") ||
      ip.startsWith("192.168.") ||
      ip.startsWith("172.16.") ||
      ip.startsWith("172.17.") ||
      ip.startsWith("172.18.") ||
      ip.startsWith("172.19.") ||
      ip.startsWith("172.20.") ||
      ip.startsWith("172.21.") ||
      ip.startsWith("172.22.") ||
      ip.startsWith("172.23.") ||
      ip.startsWith("172.24.") ||
      ip.startsWith("172.25.") ||
      ip.startsWith("172.26.") ||
      ip.startsWith("172.27.") ||
      ip.startsWith("172.28.") ||
      ip.startsWith("172.29.") ||
      ip.startsWith("172.30.") ||
      ip.startsWith("172.31.") ||
      ip == "::1"
    ) {
      // not an internet IP!
      return;
    }

    if (ipCache[ip]) {
      handleIpGeoResponse(ip, ipCache[ip]);
      return;
    }
    websocket.current.sendCommand("ipgeo " + ip);
  }*/
  function pingIp(ip, type) {}
  function handleIpGeoResponse(ip, data) {}

  const [popup, setPopup] = useState({
    x: 0,
    y: 0,
    path: null,
    extraProps: {},
    visible: false,
  });
  const [threadData, setThreadData] = useState(null);
  const [memoryData, setMemoryData] = useState(null);
  const [commandData, tunnelModal] = useState(null);
  const [commandRunData, tunnelStats] = useState(null);
  const [target, setTarget] = useState(null);
  const [targetIsTop, setTargetIsTop] = useState(undefined);

  const containerRef = useRef(null);
  const surfaceRef = useRef(null);
  const websocket = useRef(null);

  const [landPolygons, setLandPolygons] = useState([]);

  useEffect(() => {
    // load data
    fetch("//unpkg.com/world-atlas/land-110m.json")
      .then((res) => res.json())
      .then((landTopo) => {
        setLandPolygons(
          topojson.feature(landTopo, landTopo.objects.land).features
        );
      });
  }, []);

  useEffect(() => {
    // The renderer owns the drawing surface and the layer skeleton, so it has to
    // exist before anything that registers a frame proc against those layers.
    // Registration order matters and is not otherwise enforced: solveLayout must
    // land ahead of autoSize and of everything that consumes its output.
    const renderer = createRenderer(surfaceRef.current);

    setLineContainer(renderer.lineGroup);

    initialise(setPopup);

    // Reading clientWidth/clientHeight flushes pending style and layout for the
    // whole document. With tens of thousands of live SVG elements that costs
    // hundreds of milliseconds, so the size is measured only when it can
    // actually have changed and cached for the frame loop to use.
    const containerSize = { width: 0, height: 0 };

    function sizeSvg() {
      const container = containerRef.current;
      const width = container.clientWidth;
      const height = container.clientHeight;

      containerSize.width = width;
      containerSize.height = height;
      setViewport(width, height);

      renderer.setViewport(width, height);
    }
    window.addEventListener("resize", sizeSvg);
    sizeSvg();

    function closeItemMenuAndPropagate() {
      setPopup({ ...popup, visible: false });
    }

    function itemMenu(evt) {
      evt.preventDefault();
      evt.stopPropagation();
      const x = evt.currentTarget.scrollLeft + evt.clientX;
      const y = evt.currentTarget.scrollTop + evt.clientY;
      openItemMenu(evt.target, x, y);
      return false;
    }

    document.addEventListener("keypress", (evt) => {
      if (evt.key == "l") {
        cycleLayoutMode();
        layout();
      }
    });

    setTargetHandlers(
      (t) => {
        setTarget(t);
        setTargetIsTop(t == null ? undefined : t.isTop);
      },
      (t) => {
        setTargetIsTop(t == null ? undefined : t.isTop);
      }
    );

    renderer.root.addEventListener("contextmenu", closeItemMenuAndPropagate);
    renderer.root.addEventListener("mousedown", closeItemMenuAndPropagate);
    renderer.root.addEventListener("mousedown", (e) => {
      // left mouse only
      if (e.button != 0) return;
      lockTarget(null);
    });

    /*
    d3.select(svg.current).call(
      d3.zoom().on("zoom", (e) => {
        zoomer.current.attr("transform", e.transform);
        lineZoomer.current.attr("transform", e.transform);
        //parallax.current.attr("transform", "scale(0.5) " + e.transform);
      })
    );*/

    let curScale = 1;
    const scalePerFrame = 0.0002;

    let curX = 0;
    let curY = 0;
    const ofsPerFrame = 0.1;

    frameProcs.autoSize = (delta) => {
      //if (!window.autosize) return;

      const width = containerSize.width;
      const height = containerSize.height;

      mainBounds[0] = mainBounds[0] - 20;
      mainBounds[1] = mainBounds[1] - 20;
      mainBounds[2] = mainBounds[2] + 20;
      mainBounds[3] = mainBounds[3] + 20;

      const mainWidth = mainBounds[2] - mainBounds[0];
      const mainHeight = mainBounds[3] - mainBounds[1];
      const mainMidX = (mainBounds[2] + mainBounds[0]) / 2;
      const mainMidY = (mainBounds[3] + mainBounds[1]) / 2;

      const scaleX = (width - 500) / mainWidth;
      const scaleY = (height - 80) / mainHeight;
      const scale = Math.min(scaleX, scaleY);

      if (curScale < scale * 0.9) {
        curScale += scalePerFrame * delta;
        if (curScale > scale) curScale = scale;
      } else if (curScale > scale * 1.1) {
        curScale -= scalePerFrame * delta;
        if (curScale < scale) curScale = scale;
      }

      if (curX < mainMidX) {
        curX += ofsPerFrame * delta * curScale;
        if (curX > mainMidX) curX = mainMidX;
      } else if (curX > mainMidX) {
        curX -= ofsPerFrame * delta * curScale;
        if (curX < mainMidX) curX = mainMidX;
      }

      if (curY < mainMidY) {
        curY += ofsPerFrame * delta * curScale;
        if (curY > mainMidY) curY = mainMidY;
      } else if (curY > mainMidY) {
        curY -= ofsPerFrame * delta * curScale;
        if (curY < mainMidY) curY = mainMidY;
      }

      const transform = `scale(${curScale}) translate(${-curX}, ${-curY})`;
      //const transform = `scale(0.1)`;

      setTransform(curScale, curX, curY);

      renderer.main.setAttribute("transform", transform);
      renderer.lines.setAttribute("transform", transform);

      /*
      const bbox = document.getElementById("bbox");
      bbox.setAttribute("x", mainBounds[0]);
      bbox.setAttribute("y", mainBounds[1]);
      bbox.setAttribute("width", mainBounds[2] - mainBounds[0]);
      bbox.setAttribute("height", mainBounds[3] - mainBounds[1]);*/
    };

    const ui = {
      setThreadData,
      setMemoryData,
      tunnelModal,
      tunnelStats,
      pingIp,
      handleIpGeoResponse,
    };

    websocket.current = startWebsocket(
      /* statusUpdated */
      (status) => {
        const e = renderer.statusText;
        switch (status) {
          case "AccessDenied":
            e.text("Access Denied")
              .attr("fill", "#d77")
              .attr("opacity", 0.8)
              .attr("style", "display: inline");
            animations.add({
              type: "fade-message-denied",
              start: previousTimeStamp + 1000,
              duration: 1000,
              from: 0.8,
              to: 0,
              frame: (v, done) => {
                e.attr("opacity", v);
                if (done) e.attr("style", "display: none");
              },
            });
            break;
          case "Connected":
            e.text("Connected")
              .attr("fill", "#7d7")
              .attr("opacity", 0.8)
              .attr("style", "display: inline");
            animations.add({
              type: "fade-message-connected",
              start: previousTimeStamp + 1000,
              duration: 1000,
              from: 0.8,
              to: 0,
              frame: (v, done) => {
                e.attr("opacity", v);
                if (done) e.attr("style", "display: none");
              },
            });
            break;
          case "Disconnected":
            e.text("Disconnected")
              .attr("fill", "#d77")
              .attr("opacity", 0.8)
              .attr("style", "display: inline");
            break;
          case "Unauthenticated":
            e.text("Unauthenticated")
              .attr("fill", "#d77")
              .attr("opacity", 0.8)
              .attr("style", "display: inline");
            break;
          case "Connecting":
            e.text("Connecting...")
              .attr("fill", "#77d")
              .attr("opacity", 0.8)
              .attr("style", "display: inline");
            break;
          case "Authenticating":
            e.text("Authenticating...")
              .attr("fill", "#7dd")
              .attr("opacity", 0.8)
              .attr("style", "display: inline");
            break;
        }
      },

      /* objectAdded */
      (newObj) => {
        const type = schema.objectTypes[newObj.type];
        if (type.render && type.render.enter) {
          newObj.elem = renderer.main
            .append("g")
            .call((g) => g.addEventListener("contextmenu", itemMenu))
            .attr("id", newObj.id.replaceAll("/", "_"))
            .attr("data-path", newObj.id)
            .attr("opacity", 0);

          newObj.elem.addEventListener("mouseenter", () =>
            browseTarget(newObj)
          );
          newObj.elem.addEventListener("mouseleave", () => browseTarget(null));
          newObj.elem.addEventListener("mousedown", (e) => {
            // left mouse only
            if (e.button != 0) return;
            e.preventDefault();
            e.stopPropagation();
            lockTarget(newObj);
            return false;
          });

          animations.add({
            type: "fade-in-object",
            start: previousTimeStamp,
            duration: 250,
            from: 0,
            to: 1,
            frame: (v) => {
              newObj.elem.attr("opacity", v);
            },
          });
          type.render.enter(newObj, ui);

          // test code to render bounding circles (note: not tested for radius being a func)
          //newObj.elem.append("circle").attr("fill","none").attr("stroke","red").attr("r", type.layout.radius);

          layout();
        }
      },

      /* objectUpdated */
      (changedObj) => {
        const type = schema.objectTypes[changedObj.type];
        if (type.render && type.render.update) {
          type.render.update(changedObj);
        }
      },

      /* objectDeleted */
      (deletedObj) => {
        const type = schema.objectTypes[deletedObj.type];
        if (type.render && type.render.exit) {
          type.render.exit(deletedObj);
        } else if (deletedObj.elem) {
          renderer.main.removeChild(deletedObj.elem);
        } else {
          console.warn("no element to delete / no exit", deletedObj.id);
        }
        if (deletedObj.linkElem) {
          renderer.lineGroup.removeChild(deletedObj.linkElem);
        }
        layout();
      },

      /* ui */
      ui
    );
  }, []);

  function setFocus(path, includeParents) {
    setLayoutRoot(includeParents ? borg.id : path);
    websocket.current.setFocus(path, includeParents);
  }

  function setFilter(c, v) {
    websocket.current.setFilter(c, v);
  }

  window.setFocus = setFocus;
  window.setFilter = setFilter;

  const renderHeadDefault = (t) => t.label;
  const renderBodyDefault = (t) =>
    t.data ? (
      <pre>
        {t.data[0] == "{"
          ? JSON.stringify(JSON.parse(t.data), null, 2)
          : t.data}
      </pre>
    ) : (
      <p>No information</p>
    );

  const renderTarget = (t, isTop) => {
    const typeSchema = schema.objectTypes[t.type];
    const renderHead = typeSchema.renderInfoHead || renderHeadDefault;
    const renderBody = typeSchema.renderInfoBody || renderBodyDefault;

    return (
      <InfoBox isTop={isTop} renderHead={() => renderHead(t, isTop)}>
        {renderBody(t, isTop)}
      </InfoBox>
    );
  };

  return (
    <React.Fragment>
      <div
        style={{
          width: document.body.clientHeight * 0.2,
          height: document.body.clientHeight * 0.2,
          left: 0,
          bottom: 0,
          position: "absolute",
          borderRadius: "30px",
          overflow: "hidden",
        }}
      >
        {/*
        <Globe
          ref={globeEl}
          backgroundColor="rgba(0,0,0,0)"
          showGlobe={false}
          showAtmosphere={false}
          polygonsData={landPolygons}
          polygonCapMaterial={polygonsMaterial}
          width={document.body.clientHeight * 0.2}
          height={document.body.clientHeight * 0.2}
          polygonSideColor={() => "rgba(0, 0, 0, 0)"}
          pointsData={points}
          pointsTransitionDuration={0}
          pointRadius="size"
          pointResolution={7}
          pointAltitude={0.2}
          pointColor={() => "rgba(255, 0, 0, 0.25)"}
      />*/}
      </div>
      <div ref={containerRef} className="map-container">
        <div className="logo" />
        {/* The renderer appends its own drawing surface here. It sits ahead of
            the menus and panels so those keep painting over the map. */}
        <div ref={surfaceRef} className="map-surface" />
        <PerfHud />
        <RendererToggle />
        <EffectsMenu />
        <FpsCounter />
        <FilterPanel setFilter={setFilter} />
        <ItemMenu
          {...popup}
          command={(c) => websocket.current.sendCommand(c)}
          setFocus={setFocus}
          setFilter={setFilter}
          close={() => setPopup({ ...popup, visible: false })}
        />
        <ThreadModal
          data={threadData}
          open={threadData != null}
          onSubmit={() => setThreadData(null)}
          onClose={() => setThreadData(null)}
        />
        <MemoryModal
          data={memoryData}
          open={memoryData != null}
          onSubmit={() => setMemoryData(null)}
          onClose={() => setMemoryData(null)}
        />
        <TunnelModal
          data={commandData}
          open={commandData != null}
          stats={commandRunData}
          onSubmit={() => {
            commandData.handleClose && commandData.handleClose();
            tunnelModal(null);
          }}
          onClose={() => {
            commandData.handleClose && commandData.handleClose();
            tunnelModal(null);
          }}
        />
        {target && (
          <div className="targetPanel">
            <div className={targetIsTop ? "path top" : "path bottom"}>
              <pre>{target.id}</pre>
            </div>
            <div className={targetIsTop ? "info bottom" : "info top"}>
              {renderTarget(target, targetIsTop)}
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

/*
setInterval(() => {
  const allElements = document.querySelectorAll('*');
  const breakdown = {};

  allElements.forEach(el => {
      const tagName = el.tagName.toLowerCase();
      breakdown[tagName] = (breakdown[tagName] || 0) + 1;
  });

  const animbreakdown = {};
  animations.forEach(anim => {
      animbreakdown[anim.type] = (animbreakdown[anim.type] || 0) + 1;
  });

  console.clear(); // Clear the console for readability
  console.log(`Total elements: ${allElements.length}`);
  console.log('Breakdown by element type:', breakdown);
  console.log(`Number of frameprocs: ${Object.keys(frameProcs).length}`);
  console.log(`Number of animations: ${animations.length}`);
  console.log('Breakdown by animation type:', animbreakdown);

}, 1000);
*/

export default BorgMap;
